/* eslint-disable @typescript-eslint/naming-convention */
import { useGetCustomerDetailsQuery } from 'types/graphql';
import useStores from 'shared/hooks/use-stores';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import useDispensarySpecials from 'src/dispensary/hooks/use-dispensary-specials';
import { useCashBackIncentives } from 'src/payments/hooks/use-cash-back-incentives';
import { DUTCHIE_PAY, LOG_NAMESPACES } from 'src/payments/constants';
import { getLogger } from 'src/utils/datadog-logger';
import { useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';
import { ValidIncentiveValue, isValidIncentiveValue, VALID_INCENTIVE_VALUES_ARRAY } from '../shared/incentive-types';

const logger = getLogger(LOG_NAMESPACES.PBB_INCENTIVES);

export enum UserType {
  UNENROLLED = 'unenrolled',
  QUALIFIED = 'qualified',
  NOT_QUALIFIED = 'not-qualified',
}


export type IncentivesResponse = {
  incentivesActive: boolean;
  incentiveValue?: ValidIncentiveValue;
  showPaymentBanner: boolean;
  showCTABanner: boolean;
  userType: UserType;
  qualifiedPBBUser: boolean;
  payerId: string | null;
  isCashBack: boolean;
  shouldShowCashBack: boolean;
};

export function useIncentives(): IncentivesResponse {
  const flags = useFlags();
  const payByBankIncentivesEnabled = flags['fintech.platform.pay-by-bank-incentives'] ?? false;
  const payerIdEnabled = flags['core.cats-ecomm.use-payer-id-in-price-cart.rollout'] ?? false;

  const { isEnrolledDutchiePay } = useDutchiePay();
  const { Cart, User } = useStores();
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const userToken = useObserver(() => User.token);
  const isLoggingIn = useMemo(() => !!userToken && !isLoggedIn, [userToken, isLoggedIn]);
  const { specials, error: specialsError } = useDispensarySpecials({
    showPaymentRestricted: true,
    hideProductlessSpecials: false,
  });

  const { 
    shouldShowBanner: shouldShowCashBackBanner,
    cashBackIncentiveValue
  } = useCashBackIncentives();

  if (specialsError) {
    logger.error('Error fetching specials', {
      error: specialsError,
      dispensaryId: Cart.order?.dispensary?.id,
      dispensaryName: Cart.order?.dispensary?.name,
    });
  }

  const isGuestUser = !User?.exists;

  const {
    data: customerDetailsData,
    error: customerDetailsError,
    loading: customerDetailsLoading,
  } = useGetCustomerDetailsQuery({
    skip: !payByBankIncentivesEnabled || isGuestUser || !isEnrolledDutchiePay,
  });

  if (customerDetailsError) {
    logger.error('Error fetching customer details', {
      error: customerDetailsError,
      dispensaryId: Cart.order?.dispensary?.id,
      dispensaryName: Cart.order?.dispensary?.name,
      userId: User?.id,
    });
  }

  // If dispensary PBB incentives are disabled, check if cash-back incentives should still show
  if (!payByBankIncentivesEnabled) {
    return {
      incentivesActive: shouldShowCashBackBanner,
      incentiveValue: cashBackIncentiveValue,
      showPaymentBanner: shouldShowCashBackBanner,
      showCTABanner: shouldShowCashBackBanner && Cart.paymentMethod !== DUTCHIE_PAY,
      userType: UserType.NOT_QUALIFIED,
      qualifiedPBBUser: false,
      payerId: null,
      isCashBack: shouldShowCashBackBanner,
      shouldShowCashBack: Cart.paymentMethod === DUTCHIE_PAY && shouldShowCashBackBanner && (cashBackIncentiveValue ?? 0) > 0,
    };
  }

  const acceptsPBB = Cart.order?.dispensary?.paymentTypesAccepted?.dutchiePay;
  const qualifiedPBBUser = customerDetailsData?.getCustomerDetails.successfulOrderCount === 0;
  const payerId = payerIdEnabled && qualifiedPBBUser ? customerDetailsData.getCustomerDetails.externalId : null;

  let userType;
  if ((isGuestUser || !isEnrolledDutchiePay) && !customerDetailsLoading && !isLoggingIn) {
    userType = UserType.UNENROLLED;
  } else if (qualifiedPBBUser) {
    userType = UserType.QUALIFIED;
  } else {
    userType = UserType.NOT_QUALIFIED;
  }

  const payByBankIncentive = specials.find((special) => special.paymentRestrictions?.payByBankSignupIncentive);

  // Calculate and validate dispensary incentive value before casting
  const calculatedDispensaryValue = payByBankIncentive?.saleDiscounts?.[0]?.discountAmount
    ? Math.round(payByBankIncentive.saleDiscounts[0].discountAmount)
    : undefined;
  const dispensaryIncentiveValue = isValidIncentiveValue(calculatedDispensaryValue) 
    ? calculatedDispensaryValue 
    : undefined;
  
  const hasDispensaryIncentive = payByBankIncentivesEnabled && !!payByBankIncentive && !!dispensaryIncentiveValue && acceptsPBB;

  // Use dispensary incentive if available, otherwise use cash-back incentive
  // Only assign validated values to finalIncentiveValue
  let finalIncentiveValue: ValidIncentiveValue | undefined;
  if (dispensaryIncentiveValue !== undefined) {
    finalIncentiveValue = dispensaryIncentiveValue;
  } else if (cashBackIncentiveValue !== undefined) {
    finalIncentiveValue = cashBackIncentiveValue;
  }
  
  // But only mark as "active" if the dispensary incentive is valid
  const incentivesActive = hasDispensaryIncentive || shouldShowCashBackBanner;
  const isCashBack = !hasDispensaryIncentive && shouldShowCashBackBanner;

  // Separate banner logic for dispensary vs cash-back incentives
  const showPaymentBanner = hasDispensaryIncentive 
    ? (userType === UserType.UNENROLLED || userType === UserType.QUALIFIED)
    : shouldShowCashBackBanner;
  const showCTABanner = incentivesActive && Cart.paymentMethod !== DUTCHIE_PAY;
  
  // Business logic for showing cash-back display in confirmation
  const shouldShowCashBack = Cart.paymentMethod === DUTCHIE_PAY && 
                            incentivesActive && 
                            isCashBack && 
                            (finalIncentiveValue ?? 0) > 0;

  if (payByBankIncentive && !dispensaryIncentiveValue) {
    logger.error('Invalid or missing incentive value detected', {
      calculatedValue: calculatedDispensaryValue,
      incentiveValue: dispensaryIncentiveValue,
      dispensaryId: Cart.order?.dispensary?.id,
      validValues: Object.values(VALID_INCENTIVE_VALUES_ARRAY),
    });
  }
  return {
    incentivesActive,
    incentiveValue: finalIncentiveValue,
    showPaymentBanner,
    showCTABanner,
    userType,
    qualifiedPBBUser,
    payerId,
    isCashBack,
    shouldShowCashBack,
  };
}
